import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { AnimatePresence } from 'framer-motion';

import DeviceCard from './DeviceCard';
import DeviceCardSkeleton from './DeviceCardSkeleton';
import DraggableCard from './DraggableCard';

// Placeholder cards shown while the first fetch is in flight: two rows at xl, one on mobile.
const SKELETON_CARD_COUNT = 8;

interface RealtimeCardGridProps {
    cards: any[];
    loading: boolean;
    // Shown instead of the grid when there are no cards to show.
    emptyMessage: string;
    moveCard: (dragIndex: number, hoverIndex: number) => void;
    // Saves the new order once the card is dropped.
    onDrop: () => void;
    // Off while searching (indexes wouldn't map back) and for vendor accounts.
    dragDisabled: boolean;
    archived: boolean;
}

/** The device cards: skeletons while loading, a message when empty, else draggable cards. */
function RealtimeCardGrid({
    cards,
    loading,
    emptyMessage,
    moveCard,
    onDrop,
    dragDisabled,
    archived,
}: RealtimeCardGridProps) {
    return (
        <DndProvider backend={HTML5Backend}>
            <div className='mt-6 px-4 md:px-0 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-4'>
                {loading ? (
                    Array.from({ length: SKELETON_CARD_COUNT }, (_, index: number) => (
                        <DeviceCardSkeleton key={`skeleton-${index}`} />
                    ))
                ) : cards.length === 0 ? (
                    <div className='col-span-full flex h-60 items-center justify-center text-sm font-medium text-slate-500'>
                        {emptyMessage}
                    </div>
                ) : (
                    <AnimatePresence initial={false}>
                        {cards.map((record: any, index: number) => (
                            <DraggableCard
                                key={record.deviceId}
                                index={index}
                                moveCard={moveCard}
                                onDrop={onDrop}
                                disabled={dragDisabled}
                            >
                                <DeviceCard record={record} isArchived={archived} />
                            </DraggableCard>
                        ))}
                    </AnimatePresence>
                )}
            </div>
        </DndProvider>
    );
}

export default RealtimeCardGrid;
